export const navLinks = [
  { id: "home", label: "首页" },
  { id: "modes", label: "阅读方式" },
  { id: "guide", label: "阅读设置" },
  { id: "tips", label: "阅读贴士" },
  { id: "faq", label: "常见问题" },
  { id: "about", label: "关于我们" },
];

export const heroModes = [
  { icon: "ri-arrow-down-line", label: "下拉阅读" },
  { icon: "ri-file-transfer-line", label: "翻页阅读" },
  { icon: "ri-list-unordered", label: "目录跳转" },
  { icon: "ri-fullscreen-line", label: "全屏模式" },
];

export interface ReadingMode {
  icon: string;
  title: string;
  tag: string;
  desc: string;
  points: string[];
}

export const readingModes: ReadingMode[] = [
  {
    icon: "ri-arrow-down-line",
    title: "下拉阅读",
    tag: "最顺滑",
    desc: "像刷信息流一样一路下滑，画面无缝衔接，最适合追长篇连载，连贯不打断。",
    points: ["画面连续滚动，无翻页停顿", "加载顺畅，读长漫不卡壳", "适合手机竖屏沉浸追更"],
  },
  {
    icon: "ri-file-transfer-line",
    title: "翻页阅读",
    tag: "最传统",
    desc: "左右滑动手势一页一页翻，保留纸质漫画的翻页手感，节奏由你掌控。",
    points: ["左滑下一页，右滑回上一页", "经典翻页手感，适合横屏", "可开启左右双向翻页"],
  },
  {
    icon: "ri-list-unordered",
    title: "目录跳转",
    tag: "最快",
    desc: "一键呼出章节目录，随意点击任意话数直达，想跳到哪里就跳到哪里。",
    points: ["章节列表即时定位", "支持搜索指定话数", "自动标记已读进度"],
  },
  {
    icon: "ri-fullscreen-line",
    title: "全屏模式",
    tag: "最沉浸",
    desc: "隐藏所有干扰元素，画面铺满整个屏幕，配合横屏把每一格细节看个够。",
    points: ["一键隐藏界面元素", "横竖屏自适应铺满", "长按可快速截图分享"],
  },
];

export interface GuideStep {
  icon: string;
  title: string;
  desc: string;
}

export const guideSteps: GuideStep[] = [
  {
    icon: "ri-cursor-line",
    title: "打开漫画",
    desc: "进入任意漫画详情页，点击「开始阅读」进入阅读页，正文即刻加载。",
  },
  {
    icon: "ri-settings-3-line",
    title: "选择阅读模式",
    desc: "在页面底部或右上角打开「阅读设置」，在下拉与翻页之间按喜好切换。",
  },
  {
    icon: "ri-list-check-2",
    title: "用目录精准跳转",
    desc: "点击目录图标展开章节列表，选择要读的话数，进度条会同步更新。",
  },
  {
    icon: "ri-fullscreen-exit-line",
    title: "开启全屏沉浸",
    desc: "点击全屏按钮隐藏工具栏，横屏观看效果最佳，画面铺满无干扰。",
  },
  {
    icon: "ri-contrast-2-line",
    title: "调好亮度与字号",
    desc: "阅读设置里可调背景色、亮度与字号，夜间模式保护眼睛更舒服。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "进度自动保存",
    desc: "阅读位置自动记录，下次进入继续接着看，追更不断档。",
  },
];

export const settingItems = [
  { icon: "ri-exchange-line", label: "阅读模式", desc: "下拉 / 翻页切换" },
  { icon: "ri-font-size", label: "字号大小", desc: "调整正文与对话字号" },
  { icon: "ri-sun-line", label: "亮度调节", desc: "滑杆控制屏幕亮度" },
  { icon: "ri-moon-line", label: "夜间模式", desc: "深色背景护眼阅读" },
  { icon: "ri-palette-line", label: "背景色", desc: "米白、浅灰、纯黑可选" },
  { icon: "ri-bookmark-line", label: "进度保存", desc: "自动记录阅读位置" },
];

export interface Tip {
  icon: string;
  title: string;
  desc: string;
}

export const tips: Tip[] = [
  {
    icon: "ri-smartphone-line",
    title: "竖屏下拉更流畅",
    desc: "手机竖屏时建议使用下拉模式，画面连续滚动最省心，追更一气呵成。",
  },
  {
    icon: "ri-arrow-left-right-line",
    title: "横屏翻页更经典",
    desc: "平板或横屏时翻页模式更有纸质漫画手感，配合全屏效果更佳。",
  },
  {
    icon: "ri-moon-clear-line",
    title: "夜间开启护眼",
    desc: "睡前阅读记得开启夜间模式并调低亮度，减少屏幕蓝光刺激，追更不伤眼。",
  },
  {
    icon: "ri-wifi-line",
    title: "善用预加载",
    desc: "网络不稳时建议开启预加载，提前缓存下一话，遇到弱网也不怕卡壳。",
  },
];

export interface Faq {
  question: string;
  answer: string;
}

export const faqs: Faq[] = [
  {
    question: "66漫画网怎么读？",
    answer:
      "进入漫画详情页点击「开始阅读」即可进入阅读页。在阅读页底部或右上角打开「阅读设置」，选择你习惯的阅读模式（下拉或翻页），再按提示调整字号、亮度、背景色即可顺畅看完全篇，全程不卡壳。",
  },
  {
    question: "下拉和翻页两种模式有什么区别？",
    answer:
      "下拉阅读让画面像刷信息流一样连续滚动，适合竖屏手机追长篇；翻页阅读则保留纸质漫画的翻页手感，适合横屏和喜欢一页页看的读者。两者可以在阅读设置里随时切换，无需重新加载。",
  },
  {
    question: "怎么用目录快速跳转到指定章节？",
    answer:
      "阅读页内点击「目录」图标即可展开完整章节列表，点击任意话数即可直达。目录中已读章节会自动标记进度，你也可以搜索想读的话数精准定位。",
  },
  {
    question: "怎么开启全屏阅读？",
    answer:
      "阅读页点击「全屏」按钮即可隐藏工具栏、画面铺满整个屏幕。配合横屏使用效果最佳，可以看清每一格的细节；再次点击或点按任意位置即可退出全屏。",
  },
  {
    question: "阅读进度会自动保存吗？",
    answer:
      "会的。你的阅读位置会自动记录，下次进入同一部漫画时会提示「继续阅读」，点击即可从上次读到的地方无缝接续，追更不断档。",
  },
  {
    question: "阅读设置里都能调整哪些内容？",
    answer:
      "阅读设置支持切换阅读模式（下拉/翻页）、调整字号大小、调节屏幕亮度、切换背景色以及开启夜间模式，所有设置即时生效，退出后自动保留你的偏好。",
  },
];

export const aboutFeatures = [
  { icon: "ri-book-2-line", title: "海量漫画", desc: "涵盖热血、恋爱、悬疑、搞笑等丰富题材" },
  { icon: "ri-settings-4-line", title: "灵活阅读", desc: "下拉、翻页、目录、全屏随心切换" },
  { icon: "ri-flashlight-line", title: "流畅体验", desc: "预加载优化，弱网追更也不卡壳" },
  { icon: "ri-heart-3-line", title: "贴心护眼", desc: "夜间模式与亮度调节保护视力" },
];